import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { ToolIconsV4 } from '@/shared/ui/GeneralIcons/GeneralIcons.v4';
import { toolBadgeVariants, toToolBadgeCategory } from '@/shared/ui/ToolBadge';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { VARIANT_MAP } from './AgentAvatar.constants';
import { AgentAvatarProps, AgentAvatarVariant } from './AgentAvatar.types';

export const AgentAvatar = (props: AgentAvatarProps) => {
  const { variant = 'normal', avatar, tag, currentAgent = null } = props;
  const agent = currentAgent;

  // Fetch signed URL if avatar is assetId type
  const { downloadUrl, isLoading } = useGetDownloadUrl(
    avatar?.type === 'assetId' ? avatar.assetId : undefined,
  );

  // If agentId prop is provided, use it directly
  // Otherwise fall back to context agent
  if (!props.agentId && !agent) {
    return (
      <span
        data-testid="agent-icon-loading"
        className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
      >
        ?
      </span>
    );
  }

  // Use agentId prop if provided, otherwise use agent.id from context
  const agentId = props.agentId ?? agent?.id;
  const agentTitle = props.agentTitle ?? agent?.title;
  // Use tag from props or fall back to context agent's tag
  const agentTag = props.agentId ? tag : (tag ?? agent?.tag);

  // Determine the image URL based on avatar type
  const customImageUrl =
    avatar?.type === 'imageUrl'
      ? avatar.imageUrl
      : avatar?.type === 'assetId'
        ? downloadUrl
        : undefined;

  // Show custom image if available and not loading
  const shouldShowCustomImage = customImageUrl && !(avatar?.type === 'assetId' && isLoading);

  // A blank aria-label is skipped by the accessible-name computation and
  // `role="img"` forbids falling back to content, so an untitled avatar would be
  // exposed as an unnamed graphic. Without a name it stays decorative instead.
  const accessibleName = agentTitle?.trim() ? agentTitle : undefined;

  const avatarElement = (
    // The whole avatar is one image to a screen reader: the wrapper carries the
    // name the tooltip shows, so the picture inside stays decorative.
    <span
      role={accessibleName ? 'img' : undefined}
      aria-label={accessibleName}
      data-testid="agent-icon"
      data-agent-id={agentId}
      className={
        shouldShowCustomImage
          ? 'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full'
          : toolBadgeVariants({
              category: toToolBadgeCategory(agentTag),
              shape: 'circle',
              className: 'overflow-hidden',
            })
      }
      style={getSizeStyles(variant)}
    >
      {shouldShowCustomImage ? (
        <img
          src={customImageUrl}
          alt=""
          data-testid="agent-icon-image"
          className="size-full object-cover"
        />
      ) : (
        ToolIconsV4(
          agentId ?? '',
          toToolBadgeCategory(agentTag),
          VARIANT_MAP[variant].iconSize,
          undefined,
          'currentColor',
        )
      )}
    </span>
  );

  if (!accessibleName) return avatarElement;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{avatarElement}</TooltipTrigger>
        <TooltipContent side="top">{accessibleName}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Sizes come from `VARIANT_MAP`, which the design system exposes no token for,
// so there is no class to name them with.
function getSizeStyles(variant: AgentAvatarVariant) {
  const { width, height, margin } = VARIANT_MAP[variant];
  return { width, height, margin: margin || '0' };
}
